<template>
  <div class="schedule-list-container">
    <div class="header-card">
      <div class="header-content">
        <div class="left-section">
          <div class="title-with-badge">
            <h1>排课系统</h1>
            <el-tag type="success" effect="dark" class="premium-badge">从基础信息开始</el-tag>
          </div>
          <p class="subtitle">先建好本次排课，再按顺序填写年级、班级、学科、教师和课时，最后生成课表并按需调整。</p>
          <div class="quick-steps">
            <span>1. 新建排课</span>
            <span>2. 填基础信息</span>
            <span>3. 填任课安排</span>
            <span>4. 生成课表</span>
            <span>5. 调整并导出</span>
          </div>
        </div>
        <el-button type="primary" size="large" class="create-btn" :disabled="!isEditorOrAdmin" @click="openCreateDialog">
          <el-icon><Plus /></el-icon> 新建排课
        </el-button>
      </div>
    </div>

    <!-- Workspace Grid -->
    <div v-loading="loading" class="workspace-grid">
      <div v-if="hasLoadedWorkspaces && workspaces.length === 0" class="empty-state">
        <el-empty description="还没有排课任务，先新建一个吧。">
          <el-button type="primary" :disabled="!isEditorOrAdmin" @click="openCreateDialog">立即新建</el-button>
        </el-empty>
      </div>
      
      <div v-for="ws in workspaces" :key="ws.id" class="workspace-card animate__animated animate__fadeIn">
        <div class="card-glow"></div>
        <div class="card-header">
          <div class="title-row">
            <h3 class="ws-name">{{ ws.name }}</h3>
            <el-tag :type="getStatusType(ws.status)" effect="light">
              {{ getStatusText(ws.status) }}
            </el-tag>
          </div>
          <span class="created-time">创建时间：{{ formatDateTime(ws.created_at) }}</span>
        </div>

        <div class="card-body">
          <div class="stat-row">
            <div class="stat-item">
              <span class="stat-label">每周天数</span>
              <span class="stat-value">{{ ws.config?.days_per_week || 5 }}天</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">日总课时</span>
              <span class="stat-value">{{ getDailyPeriods(ws.config) }}节</span>
            </div>
            <div class="stat-item">
              <span class="stat-label">关联学期</span>
              <span class="stat-value-text">{{ getSemesterName(ws.semester_id) }}</span>
            </div>
          </div>

          <div class="progress-section">
            <div class="progress-labels">
            <span>生成进度</span>
              <span>{{ ws.status === 'success' ? 100 : (ws.progress || 0) }}%</span>
            </div>
            <el-progress 
              :percentage="ws.status === 'success' ? 100 : (ws.progress || 0)" 
              :status="ws.status === 'failed' ? 'exception' : (ws.status === 'success' ? 'success' : '')"
              :stroke-width="6" 
              :show-text="false"
            />
            <p class="log-preview" :class="{ 'error-log': ws.status === 'failed' }">
              {{ ws.status === 'failed' ? ws.error_message : (ws.log_message || getNextStepText(ws)) }}
            </p>
          </div>
        </div>

        <div class="card-footer">
          <el-button type="info" plain size="default" :disabled="!isEditorOrAdmin" @click="openEditDialog(ws)">
            修改设置
          </el-button>
          <el-button type="danger" plain size="default" :disabled="!isAdministrator" @click="handleDelete(ws.id)">
            删除
          </el-button>
          <el-button type="primary" size="default" class="enter-btn-real" @click="enterWorkspace(ws.id)">
            进入工作区 <el-icon><ArrowRight /></el-icon>
          </el-button>
        </div>
      </div>
    </div>

    <!-- Create/Edit Workspace Dialog -->
    <el-dialog
      v-model="dialogVisible"
      :title="isEdit ? '修改排课设置' : '新建排课'"
      width="600px"
      destroy-on-close
    >
      <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
        <el-form-item label="名称" prop="name">
          <el-input v-model="form.name" placeholder="例如：2026学年第一学期高一排课" />
        </el-form-item>

        <el-form-item label="关联学期" prop="semester_id">
          <div style="display: flex; gap: 10px; align-items: center; width: 100%;">
            <el-select v-model="form.semester_id" placeholder="请选择学期" style="flex: 1;">
              <el-option 
                v-for="s in semesters" 
                :key="s.id" 
                :label="s.name" 
                :value="s.id" 
              />
            </el-select>
            <el-button type="success" plain @click="handleQuickCreateSemester">
              <el-icon><Plus /></el-icon> 新建学期
            </el-button>
          </div>
        </el-form-item>

        <el-form-item v-if="!isEdit" label="省份/学段预设模板（可选）" prop="preset_profile">
          <el-select v-model="form.preset_profile" placeholder="选择预设包可以一键生成科目、课时、作息及约束规则" clearable style="width: 100%;">
            <el-option
              v-for="p in presetProfiles"
              :key="p.id"
              :label="p.name"
              :value="p.id"
            >
              <div style="display: flex; justify-content: space-between; align-items: center; width: 100%;">
                <span>{{ p.name }}</span>
                <span style="color: #909399; font-size: 12px;">{{ p.gaokao_mode === '3+3' ? '新高考' : '常规高考' }}</span>
              </div>
            </el-option>
          </el-select>
          <div v-if="form.preset_profile" style="margin-top: 8px; font-size: 12px; color: #e6a23c; line-height: 1.4;">
            提示：{{ getSelectedPresetDesc(form.preset_profile) }}
          </div>
        </el-form-item>

        <div class="section-divider">基础作息</div>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="每周排课天数" prop="days_per_week">
              <el-input-number v-model="form.days_per_week" :min="4" :max="7" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="上午课节数" prop="periods_morning">
              <el-input-number v-model="form.periods_morning" :min="2" :max="5" style="width: 100%" @change="val => updateGlobalPeriod('morning', val)" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="下午课节数" prop="periods_afternoon">
              <el-input-number v-model="form.periods_afternoon" :min="1" :max="5" style="width: 100%" @change="val => updateGlobalPeriod('afternoon', val)" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="晚上课节数(选填)" prop="periods_evening">
              <el-input-number v-model="form.periods_evening" :min="0" :max="4" style="width: 100%" @change="val => updateGlobalPeriod('evening', val)" />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="section-divider">功能与周次设置</div>
        <el-row :gutter="20">
          <el-col :span="8">
            <el-form-item label="学期总周数" prop="total_weeks">
              <el-input-number v-model="form.total_weeks" :min="1" :max="40" style="width: 100%" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="启用体育合班" prop="enable_sports_combined">
              <el-switch v-model="form.enable_sports_combined" active-text="启用" inactive-text="禁用" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="启用走班拼盘" prop="enable_walk_class">
              <el-switch v-model="form.enable_walk_class" active-text="启用" inactive-text="禁用" />
            </el-form-item>
          </el-col>
        </el-row>

        <div class="section-divider">按天微调课节数</div>
        <div class="day-period-grid">
          <div class="day-period-head">
            <span>星期</span>
            <span>上午</span>
            <span>下午</span>
            <span>晚上</span>
          </div>
          <div v-for="day in form.days_per_week" :key="day" class="day-period-row">
            <span class="day-label">{{ getDayName(day) }}</span>
            <el-input-number v-model="form.day_period_counts[String(day)].morning" :min="0" :max="8" controls-position="right" />
            <el-input-number v-model="form.day_period_counts[String(day)].afternoon" :min="0" :max="8" controls-position="right" />
            <el-input-number v-model="form.day_period_counts[String(day)].evening" :min="0" :max="6" controls-position="right" />
          </div>
        </div>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="submitting" @click="submitForm">{{ isEdit ? '保存修改' : '创建并进入' }}</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Plus, ArrowRight } from '@element-plus/icons-vue'
import { courseScheduleApi, semesterApi } from '@/api'
import { ElMessage, ElMessageBox } from 'element-plus'
import { getUser } from '@/utils/auth'

const router = useRouter()
const loading = ref(false)
const submitting = ref(false)
const workspaces = ref([])
const semesters = ref([])
const hasLoadedWorkspaces = ref(false)

const currentUser = ref(getUser())
const isEditorOrAdmin = computed(() => {
  const u = currentUser.value
  return u && (u.is_admin || u.role === 'admin' || u.role === 'editor')
})
const isAdministrator = computed(() => {
  const u = currentUser.value
  return u && (u.is_admin || u.role === 'admin')
})

// Dialog values
const dialogVisible = ref(false)
const isEdit = ref(false)
const editId = ref(null)
const formRef = ref(null)

const form = ref({
  name: '',
  semester_id: '',
  days_per_week: 5,
  periods_morning: 4,
  periods_afternoon: 4,
  periods_evening: 0,
  enable_sports_combined: true,
  enable_walk_class: true,
  total_weeks: 25,
  day_period_counts: {
    "1": { morning: 4, afternoon: 4, evening: 0 },
    "2": { morning: 4, afternoon: 4, evening: 0 },
    "3": { morning: 4, afternoon: 4, evening: 0 },
    "4": { morning: 4, afternoon: 4, evening: 0 },
    "5": { morning: 4, afternoon: 4, evening: 0 }
  }
})

const rules = {
  name: [
    { required: true, message: '请输入排课名称', trigger: 'blur' },
    { min: 2, max: 50, message: '名称长度在 2 到 50 个字符之间', trigger: 'blur' }
  ],
  semester_id: [
    { required: true, message: '请选择关联学期', trigger: 'change' }
  ]
}

const presetProfiles = ref([])

const fetchPresetProfiles = async () => {
  try {
    const data = await courseScheduleApi.getPresets()
    presetProfiles.value = data
  } catch (error) {
    console.error('Failed to load preset profiles:', error)
  }
}

const getSelectedPresetDesc = (presetId) => {
  const p = presetProfiles.value.find(item => item.id === presetId)
  return p ? p.description : ''
}

onMounted(() => {
  fetchSemesters()
  fetchWorkspaces()
  fetchPresetProfiles()
})

const getDefaultDayCounts = () => ({
  morning: Number(form.value.periods_morning || 0),
  afternoon: Number(form.value.periods_afternoon || 0),
  evening: Number(form.value.periods_evening || 0)
})

const normalizeFormDayCounts = (resetMissing = false) => {
  const next = {}
  const existing = form.value.day_period_counts || {}
  for (let day = 1; day <= Number(form.value.days_per_week || 0); day++) {
    const oldCfg = existing[String(day)] || {}
    const defaults = getDefaultDayCounts()
    next[String(day)] = resetMissing
      ? defaults
      : {
          morning: Number(oldCfg.morning ?? defaults.morning),
          afternoon: Number(oldCfg.afternoon ?? defaults.afternoon),
          evening: Number(oldCfg.evening ?? defaults.evening)
        }
  }
  form.value.day_period_counts = next
}

const getDayName = (day) => ['一', '二', '三', '四', '五', '六', '日'][day - 1] ? `星期${['一', '二', '三', '四', '五', '六', '日'][day - 1]}` : `周${day}`

const updateGlobalPeriod = (type, val) => {
  if (!form.value.day_period_counts) return
  for (let day = 1; day <= Number(form.value.days_per_week || 0); day++) {
    if (!form.value.day_period_counts[String(day)]) {
      form.value.day_period_counts[String(day)] = getDefaultDayCounts()
    }
    form.value.day_period_counts[String(day)][type] = Number(val)
  }
}

watch(
  () => form.value.days_per_week,
  () => normalizeFormDayCounts(false)
)

const fetchSemesters = async () => {
  try {
    const data = await semesterApi.getAll()
    semesters.value = data
    // 默认选择当前激活的学期
    const current = data.find(s => s.is_active)
    if (current && !form.value.semester_id) {
      form.value.semester_id = current.id
    }
  } catch (error) {
    console.error('Failed to load semesters', error)
  }
}

const fetchWorkspaces = async () => {
  loading.value = true
  try {
    const data = await courseScheduleApi.getWorkspaces()
    workspaces.value = data
  } catch (error) {
    console.error('Failed to get workspaces:', error)
  } finally {
    loading.value = false
    hasLoadedWorkspaces.value = true
  }
}

const handleQuickCreateSemester = () => {
  ElMessageBox.prompt('请输入新学期的名称 (例如：2026学年秋季学期)', '快速新建学期', {
    confirmButtonText: '确定创建',
    cancelButtonText: '取消',
    inputPattern: /\S+/,
    inputErrorMessage: '学期名称不能为空'
  }).then(async ({ value }) => {
    try {
      const today = new Date()
      const future = new Date()
      future.setMonth(today.getMonth() + 5)
      
      const formatDate = (date) => {
        const y = date.getFullYear()
        const m = String(date.getMonth() + 1).padStart(2, '0')
        const d = String(date.getDate()).padStart(2, '0')
        return `${y}-${m}-${d}`
      }

      const newSem = await semesterApi.create({
        name: value.trim(),
        start_date: formatDate(today),
        end_date: formatDate(future),
        is_active: false,
        available_grades: "高一,高二,高三"
      })
      
      ElMessage.success(`学期【${newSem.name}】创建成功`)
      
      // 重新加载学期列表并自动选中
      await fetchSemesters()
      form.value.semester_id = newSem.id
    } catch (error) {
      console.error(error)
      ElMessage.error('创建学期失败，请重试')
    }
  }).catch(() => {})
}

const openCreateDialog = () => {
  isEdit.value = false
  editId.value = null
  form.value = {
    name: '',
    semester_id: semesters.value.find(s => s.is_active)?.id || '',
    days_per_week: 5,
    periods_morning: 4,
    periods_afternoon: 4,
    periods_evening: 0,
    preset_profile: '',
    enable_sports_combined: true,
    enable_walk_class: true,
    total_weeks: 25,
    day_period_counts: {}
  }
  normalizeFormDayCounts(true)
  dialogVisible.value = true
}

const openEditDialog = (ws) => {
  isEdit.value = true
  editId.value = ws.id
  
  const config = ws.config || {}
  form.value = {
    name: ws.name,
    semester_id: ws.semester_id,
    days_per_week: config.days_per_week || 5,
    periods_morning: config.periods_morning || 4,
    periods_afternoon: config.periods_afternoon || 4,
    periods_evening: config.periods_evening || 0,
    enable_sports_combined: config.enable_sports_combined !== false,
    enable_walk_class: config.enable_walk_class !== false,
    total_weeks: config.total_weeks || 25,
    day_period_counts: config.day_period_counts || {}
  }
  normalizeFormDayCounts(false)
  dialogVisible.value = true
}

const getDailyPeriods = (config) => {
  if (!config) return 8
  const globalTotal = (config.periods_morning || 0) + (config.periods_afternoon || 0) + (config.periods_evening || 0)
  const dayCounts = config.day_period_counts || {}
  const dayTotals = Object.values(dayCounts).map(cfg => Number(cfg.morning || 0) + Number(cfg.afternoon || 0) + Number(cfg.evening || 0))
  return Math.max(globalTotal, ...dayTotals)
}

const getSemesterName = (semesterId) => {
  const sem = semesters.value.find(s => s.id === semesterId)
  return sem ? sem.name : '未知学期'
}

const submitForm = async () => {
  if (!formRef.value) return
  
  await formRef.value.validate(async (valid) => {
    if (!valid) return
    
    submitting.value = true
    normalizeFormDayCounts(false)
    const payload = {
      name: form.value.name,
      semester_id: form.value.semester_id,
      preset_profile: form.value.preset_profile || undefined,
      config: {
        days_per_week: Number(form.value.days_per_week),
        periods_morning: Number(form.value.periods_morning),
        periods_afternoon: Number(form.value.periods_afternoon),
        periods_evening: Number(form.value.periods_evening),
        day_period_counts: form.value.day_period_counts,
        enable_sports_combined: form.value.enable_sports_combined,
        enable_walk_class: form.value.enable_walk_class,
        total_weeks: Number(form.value.total_weeks || 25),
      }
    }
    
    try {
      if (isEdit.value) {
        await courseScheduleApi.updateWorkspace(editId.value, payload)
      ElMessage.success('排课设置已更新')
      } else {
        const created = await courseScheduleApi.createWorkspace(payload)
        ElMessage.success('排课已创建')
        router.push(`/course-schedule/${created.id}`)
      }
      dialogVisible.value = false
      fetchWorkspaces()
    } catch (error) {
      console.error(error)
    } finally {
      submitting.value = false
    }
  })
}

const handleDelete = (id) => {
  ElMessageBox.confirm(
    '确定删除这次排课吗？里面的任课安排、课表结果和保存记录都会一起删除。',
    '删除确认',
    {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning'
    }
  ).then(async () => {
    try {
      await courseScheduleApi.deleteWorkspace(id)
      ElMessage.success('工作区已删除')
      fetchWorkspaces()
    } catch (error) {
      console.error(error)
    }
  })
}

const enterWorkspace = (id) => {
  router.push(`/course-schedule/${id}`)
}

const getStatusType = (status) => {
  switch (status) {
    case 'idle': return 'info'
    case 'running': return 'primary'
    case 'success': return 'success'
    case 'failed': return 'danger'
    default: return 'info'
  }
}

const getStatusText = (status) => {
  switch (status) {
    case 'idle': return '未编排'
    case 'running': return '正在生成'
    case 'success': return '已生成'
    case 'failed': return '生成失败'
    default: return status
  }
}

const getNextStepText = (ws) => {
  if (ws.status === 'success') return '课表已经生成，可以进去查看、调整和导出。'
  if (ws.status === 'running') return '系统正在生成课表，请稍等。'
  return '下一步：进入后先填基础信息，再生成课表。'
}

const formatDateTime = (val) => {
  if (!val) return ''
  const d = new Date(val)
  return d.toLocaleString()
}
</script>

<style scoped>
.schedule-list-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 12px 0 40px 0;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

.header-card {
  background: #ffffff;
  border: 1px solid #e1e7f0;
  border-radius: 12px;
  padding: 30px 34px;
  color: #1e293b;
  box-shadow: 0 8px 28px rgba(139, 92, 246, 0.06);
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
}

.header-card::after {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  width: 240px;
  height: 100%;
  background: linear-gradient(135deg, rgba(139, 92, 246, 0.12), rgba(217, 70, 239, 0.08));
  z-index: 1;
}

.header-content {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 20px;
}

.left-section {
  max-width: 780px;
}

.title-with-badge {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.header-card h1 {
  font-size: 30px;
  font-weight: 800;
  margin: 0;
}

.premium-badge {
  font-weight: 600;
  border-radius: 6px;
  border: none;
}

.subtitle {
  font-size: 15px;
  color: #475569;
  line-height: 1.6;
  margin: 0;
}

.quick-steps {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 18px;
}

.quick-steps span {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  padding: 0 12px;
  border-radius: 6px;
  background: #f5f3ff;
  border: 1px solid #ddd6fe;
  color: #6d28d9;
  font-size: 13px;
  font-weight: 600;
}

.create-btn {
  background: #8b5cf6 !important;
  border: none !important;
  font-weight: 600;
  border-radius: 8px;
  padding: 12px 24px;
  box-shadow: 0 6px 16px rgba(139, 92, 246, 0.24);
  transition: all 0.3s ease;
  color: #ffffff !important;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(139, 92, 246, 0.28);
}

/* Grid Layout */
.workspace-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 20px;
}

.empty-state {
  grid-column: 1 / -1;
  background: #ffffff;
  padding: 60px;
  border-radius: 10px;
  border: 1px dashed #cbd5e1;
  text-align: center;
}

.workspace-card {
  background: #ffffff;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.04);
  padding: 22px;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.workspace-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(139, 92, 246, 0.08);
  border-color: #c084fc;
}

.card-glow {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 4px;
  background: linear-gradient(90deg, #8b5cf6, #d946ef);
}

.card-header {
  margin-bottom: 20px;
}

.title-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 6px;
}

.ws-name {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
  margin: 0;
  line-height: 1.3;
}

.created-time {
  font-size: 12px;
  color: #64748b;
}

.stat-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  display: flex;
  flex-direction: column;
}

.stat-item:not(:last-child) {
  border-right: 1px solid #e2e8f0;
}

.stat-label {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 18px;
  font-weight: 800;
  color: #1e293b;
}

.stat-value-text {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  height: 27px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.progress-section {
  margin-bottom: 20px;
}

.progress-labels {
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #64748b;
  margin-bottom: 6px;
}

.log-preview {
  font-size: 12px;
  color: #64748b;
  margin: 8px 0 0 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.error-log {
  color: #ef4444;
  font-weight: 500;
}

.card-footer {
  margin-top: auto;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.card-footer .el-button {
  flex: 1;
  border-radius: 7px;
}

.enter-btn-real {
  background: #8b5cf6 !important;
  border: none !important;
  color: white !important;
}
.enter-btn-real:hover {
  background: #7c3aed !important;
}

.section-divider {
  font-size: 14px;
  font-weight: 700;
  color: #475569;
  border-left: 4px solid #8b5cf6;
  padding-left: 8px;
  margin: 24px 0 16px 0;
}

.day-period-grid {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
  background: #ffffff;
}

.day-period-head,
.day-period-row {
  display: grid;
  grid-template-columns: 86px repeat(3, minmax(88px, 1fr));
  gap: 10px;
  align-items: center;
  padding: 8px 12px;
}

.day-period-head {
  background: #f8fafc;
  color: #475569;
  font-size: 12px;
  font-weight: 700;
}

.day-period-row {
  border-top: 1px solid #f1f5f9;
}

.day-period-row .el-input-number {
  width: 100%;
}

.day-label {
  color: #1e293b;
  font-size: 13px;
  font-weight: 700;
}
</style>
